import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  PrimaryFullScreenSpinner,
  toast,
} from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense, useMemo, useState } from "react";
import { RiBuilding2Line } from "react-icons/ri";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { BusinessEntityColumnCell } from "~/components/tables/BusinessEntityColumnCell";
import { graphql } from "~/gql/client";
import type { BusinessEntityKycQueueRowFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/kyc-queue")({
  head: () => opsHead("KYC Queue"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { data, refetch, loading } = useQuery(
    graphql(`
      query BusinessEntitiesWithoutKycUri {
        businessEntitiesWithoutKycUri {
          id
          ...BusinessEntityKycQueueRow
        }
      }

      fragment BusinessEntityKycQueueRow on BusinessEntity {
        id
        name
        organisation {
          id
          name
          squareLogoUrl
        }
      }
    `),
    { notifyOnNetworkStatusChange: true },
  );

  const businessEntities = data?.businessEntitiesWithoutKycUri ?? [];
  const onSuccess = useMemo(() => () => void refetch(), [refetch]);

  const columns = useMemo<ColumnDef<BusinessEntityKycQueueRowFragment>[]>(
    () => [
      {
        id: "businessEntity",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Business Entity" />
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Icon
              imageUrl={row.original.organisation?.squareLogoUrl ?? undefined}
              fallbackIcon={<RiBuilding2Line />}
              size="6"
            />
            <BusinessEntityColumnCell businessEntity={row.original} />
          </div>
        ),
      },
      {
        id: "actions",
        enableSorting: false,
        header: () => null,
        cell: ({ row }) => (
          <QueueActionsCell
            key={row.original.id}
            entity={row.original}
            onSuccess={onSuccess}
          />
        ),
      },
    ],
    [onSuccess],
  );

  if (loading && businessEntities.length === 0) {
    return <PrimaryFullScreenSpinner />;
  }

  return (
    <AdminPageContainer>
      <AdminPageHeader title="KYC Queue" />
      <DataTable
        data={businessEntities}
        columns={columns}
        isLoadingData={false}
        manualPagination={true}
        manualSorting={true}
        hidePagination={true}
        pageSize={200}
        className="overflow-hidden border"
      />
    </AdminPageContainer>
  );
}

function QueueActionsCell({
  entity,
  onSuccess,
}: {
  entity: BusinessEntityKycQueueRowFragment;
  onSuccess: () => void;
}) {
  const [kycUri, setKycUri] = useState("");
  const [confirmDismissOpen, setConfirmDismissOpen] = useState(false);

  const [updateBusinessEntityDetail, { loading: submitLoading }] = useMutation(
    graphql(`
      mutation SetBusinessEntityKycUri($input: UpdateBusinessEntityDetailInput!) {
        updateBusinessEntityDetail(input: $input) {
          businessEntity {
            id
            kycUri
          }
        }
      }
    `),
  );

  const [dismissBusinessEntityKyc, { loading: dismissLoading }] = useMutation(
    graphql(`
      mutation DismissBusinessEntityKyc($input: DismissBusinessEntityKycInput!) {
        dismissBusinessEntityKyc(input: $input) {
          businessEntity {
            id
            kycDismissedByUserProfileId
          }
        }
      }
    `),
  );

  async function handleSubmit() {
    const trimmed = kycUri.trim();
    if (!trimmed) {
      return;
    }
    await updateBusinessEntityDetail({
      variables: { input: { businessEntityId: entity.id, kycUri: trimmed } },
    });
    toast({ kind: "success", title: "KYC link set", autoHide: true });
    onSuccess();
  }

  async function handleDismiss() {
    await dismissBusinessEntityKyc({
      variables: { input: { businessEntityId: entity.id } },
    });
    setConfirmDismissOpen(false);
    toast({ kind: "success", title: "KYC dismissed", autoHide: true });
    onSuccess();
  }

  return (
    <div className="flex items-center justify-end gap-2">
      <Input
        type="url"
        placeholder="https://aiprise.com/..."
        value={kycUri}
        onChange={(e) => setKycUri(e.target.value)}
        className="w-72"
      />
      <Button
        size="sm"
        onClick={() => void handleSubmit()}
        disabled={submitLoading || !kycUri.trim()}
        loading={submitLoading}
      >
        Set link
      </Button>
      <Button
        size="sm"
        variant="destructive"
        onClick={() => setConfirmDismissOpen(true)}
        disabled={dismissLoading}
      >
        Dismiss
      </Button>
      <Dialog open={confirmDismissOpen} onOpenChange={setConfirmDismissOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dismiss KYC?</DialogTitle>
            <DialogDescription>
              This marks KYC as dismissed for <strong>{entity.name}</strong> and
              removes it from this queue. We'll note that you dismissed it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDismissOpen(false)}
              disabled={dismissLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDismiss()}
              disabled={dismissLoading}
              loading={dismissLoading}
            >
              Dismiss KYC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
